import React from 'react';

export type ABannerDemoProps = {
  siteKey: string;
  from: string;
  to: string;
  expName: string;
  setExpName: (v: string) => void;
  assigned: string;
  setAssigned: (v: string) => void;
  emitEvent: (event: 'accept' | 'reject' | 'settings') => void;
  variants?: readonly string[];
  onRefresh?: () => void;
};

export default function ABannerDemo({
  siteKey,
  from,
  to,
  expName,
  setExpName,
  assigned,
  setAssigned,
  emitEvent,
  variants = ['mb1', 'cc1', 'sp1', 'hb1', 'fs1', 'ts1'],
  onRefresh,
}: ABannerDemoProps) {
  const [open, setOpen] = React.useState(false);

  const randomVariant = React.useCallback(() => {
    const next = variants[Math.floor(Math.random() * variants.length)] ?? variants[0];
    return next;
  }, [variants]);

  return (
    <div className="rounded-lg border p-4 text-sm">
      <h2 className="text-lg font-semibold mb-2">Banner A/B Demo</h2>
      <div className="mb-2 flex gap-2 items-center">
        <button
          className="px-2 py-1 border rounded"
          onClick={() => {
            setOpen((v) => !v);
          }}
        >
          {open ? 'Hide demo' : 'Show demo'}
        </button>
        <label className="text-xs text-gray-600">
          Experiment:
          <input
            className="ml-2 border rounded px-1 py-0.5"
            value={expName}
            onChange={(e) => setExpName(e.target.value || 'cmp-banner-ab')}
            placeholder="exp name"
          />
        </label>
        <button
          className="px-2 py-1 border rounded"
          onClick={() => {
            const next = randomVariant();
            setAssigned(next);
          }}
        >
          Reset assignment
        </button>
        <label className="text-xs text-gray-600">
          Variant:
          <select
            className="ml-2 border rounded px-1 py-0.5"
            value={assigned}
            onChange={(e) => {
              const next = e.target.value;
              if ((variants as readonly string[]).includes(next)) setAssigned(next);
            }}
          >
            {variants.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <span className="text-xs text-gray-500">
          Assigned: <code>{assigned}</code>
        </span>
      </div>
      {open && (
        <div className="border rounded p-3">
          <div className="text-sm mb-2">
            Simulate Banner Actions for variant <code>{assigned}</code>:
          </div>
          <div className="flex gap-2">
            <button className="px-2 py-1 border rounded" onClick={() => emitEvent('settings')}>
              Settings
            </button>
            <button
              className="px-2 py-1 border rounded"
              onClick={() => {
                emitEvent('reject');
                setOpen(false);
              }}
            >
              Reject
            </button>
            <button
              className="px-2 py-1 border rounded bg-black text-white"
              onClick={() => {
                emitEvent('accept');
                setOpen(false);
              }}
            >
              Accept
            </button>
          </div>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button className="px-2 py-1 border rounded" onClick={() => onRefresh?.()}>
          Refresh experiments
        </button>
        <span className="text-xs text-gray-500">
          Range {from} → {to} · site <code>{siteKey}</code> · exp <code>{expName}</code> · variant{' '}
          <code>{assigned}</code>
        </span>
      </div>
    </div>
  );
}
